<template>
    <div>
        <loading-progress-bar v-if="loading" :loading="loading" color="secondary"></loading-progress-bar>
        <div v-if="!loading && equipment">
            <v-card :flat="!showCard">
                <v-card-title v-if="title" class="headline" :class="whiteMode ? '' : 'white--text'">
                    {{ title }}
                </v-card-title>
                <v-card-subtitle>
                    {{ 'Equipment' | translate }}: {{ equipment }}
                </v-card-subtitle>
                <v-card-text>
                    <v-layout row wrap>
                        <v-flex xs12>
                            <div v-if="equipmentIntegrityIssues.length === 0" class="text-xs-center pa-3">
                                <v-icon large color="success" class="mb-2">fa-check-circle</v-icon>
                                <p class="title">{{ 'No missing sensors detected' | translate }}</p>
                            </div>
                            <v-alert v-for="(integIssue, index) in equipmentIntegrityIssues"
                                     :key="index"
                                     :value="true"
                                     :color="integIssue.status"
                                     style="padding: 6px;"
                                     class="mb-2"
                            >
                                {{ integIssue.description }}
                                <v-chip v-for="(missingSensor, mIndex) in integIssue.sensors" :key="mIndex" class="ma-1">
                                    {{ missingSensor }}
                                </v-chip>
                            </v-alert>
                            <v-alert v-for="(note, nIndex) in equipmentIntegrityNotes"
                                     :key="'note-' + nIndex"
                                     :value="true"
                                     color="info"
                                     style="padding: 6px;"
                                     class="mb-2"
                            >
                                {{ 'Note on' | translate }} <strong>{{ note.sensor_name }}</strong> : {{ note.notes }}
                            </v-alert>
                        </v-flex>
                    </v-layout>
                </v-card-text>
            </v-card>
        </div>
        <div v-if="!loading && !equipment" class="text-xs-center pa-3">
            <v-icon large color="warning" class="mb-2">fa-exclamation-triangle</v-icon>
            <p class="title">{{ 'Please select an equipment' | translate }}</p>
        </div>
    </div>
</template>

<script>
import LoadingProgressBar from './../LoadingProgressBar'
import integrity from '../../config/pointMapping/integrity.json'
import { getResult, matchEquipmentTags, calculateEquipmentIntegrityIssues } from '../../helper'
import { mapGetters } from 'vuex'

export default {
    name: 'SensorIntegrityWidget',
    components: { LoadingProgressBar },
    props: {
        equipment: { type: String, default: null },
        title: { type: String, default: null },
        showCard: { type: Boolean, default: true },
        whiteMode: { type: Boolean, default: false },
    },
    data: function () {
        return {
            loading: true,
            timeout: null,
            knownExistingIssues: [],
        }
    },
    computed: {
        ...mapGetters([
            'mappingIntegrityConfig',
        ]),
        equipmentDetails() {
            if (!this.equipment) return null
            let details = this.$store.state.pointsListStore.pointsList.equipment_details
            if (details && details[this.equipment]) {
                return details[this.equipment]
            }
            return null
        },
        equipmentPoints() {
            if (!this.equipment) return []
            let sensors = this.$store.state.pointsListStore.pointsList.sensors
            if (sensors && sensors[this.equipment]) {
                return sensors[this.equipment]
            }
            return []
        },
        equipmentIntegrityIssues() {
            if (!this.equipmentDetails) return []

            let integrityConfig = this.mappingIntegrityConfig ? this.mappingIntegrityConfig : integrity
            
            return calculateEquipmentIntegrityIssues({
                equipmentDetails: this.equipmentDetails,
                equipmentPoints: this.equipmentPoints,
                integrityConfig: integrityConfig,
                knownExistingIssues: this.knownExistingIssues,
                equipmentName: this.equipment,
                translateFn: this.$options.filters.translate,
                options: { includeOptional: true }
            })
        },
        equipmentIntegrityNotes() {
            if (!this.equipment || !this.knownExistingIssues || this.knownExistingIssues.length === 0) {
                return []
            }
            return this.knownExistingIssues.filter(
                x => x.equipment_name === this.equipment && x.notes && x.notes !== ""
            )
        }
    },
    watch: {
        equipment() {
            this.refresh()
        },
    },
    mounted() {
        this.refresh()
    },
    methods: {
        debounce(callback, wait) {
            clearTimeout(this.timeout)
            this.timeout = setTimeout(() => callback(), wait)
        },
        refresh() {
            this.debounce(this.getData, 300)
        },
        async getData() {
            this.loading = true
            if (!this.equipment) {
                this.knownExistingIssues = []
                this.loading = false
                return
            }
            let facilityName = this.$store.state.pointsListStore.pointsList.facility_name
            if (!facilityName) {
                this.knownExistingIssues = []
                this.loading = false
                return
            }
            try {
                let existingResult = await getResult({
                    facility_name: facilityName,
                    result_name: 'MappingIntegrityIssues'
                })
                if (existingResult && existingResult.result && existingResult.result.length > 0) {
                    this.knownExistingIssues = existingResult.result[0].content || []
                } else {
                    this.knownExistingIssues = []
                }
            } catch (e) {
                console.error('Error fetching integrity issues:', e)
                this.knownExistingIssues = []
            }
            this.loading = false
        }
    },
}
</script>

<style scoped>
</style>
